/* eslint-disable react/jsx-no-undef */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Skeleton } from '@mui/material';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import strapiApi from '../../api/strapi';
import CustomButton from '../../components/CustomButton';
import Metatags from '../../components/MetaTags/metatags';
import ProjectCard from '../../components/ProjectCard';
import { COLORS, PATHS } from '../../constants';
import fontsStyles from '../../styles/fontStyles';
import styles from './styles';

const Header = () => {
  const classes = styles();
  const fontClasses = fontsStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.projectWrapper}>
      <Box className={classes.projectContentWrapper}>
        <Box>
          <p className={fontClasses.sectionSubTitles}>{t('projectsPage.headerSubTitle')}</p>
          <h1 className={clsx(fontClasses.h1, classes.sectionTitle)}>
            {t('projectsPage.headerTitle')}
          </h1>
          <p className={clsx(fontClasses.p, classes.sectionText)}>{t('projectsPage.headerText')}</p>
          <Box className={classes.buttonsBox}>
            <CustomButton label={t('projectsPage.contact')} url={PATHS.contact} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ProjectsContent = () => {
  const classes = styles();
  const fontClasses = fontsStyles();
  const { t, i18n } = useTranslation();
  const [projectsData, setProjectsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await strapiApi.get('/projects', {
          params: {
            sort: 'order',
            locale: i18n.language,
            'populate[images][fields][0]': 'url',
          },
        });

        // Strapi 5: response.data.data es el array, sin .attributes
        setProjectsData(response.data.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [i18n.language]);

  return (
    <Box>
      {loading && !error && (
        <Box className={classes.loadingWrapper}>
          <Box className={classes.loadingContentWrapper}>
            <Box className={classes.loadingBox}>
              <Box className={classes.loadingContentBox}>
                <Skeleton
                  variant='rectangular'
                  height={100}
                  sx={{ marginBottom: '20px', borderRadius: '10px', width: '100%' }}
                />
                <Skeleton
                  variant='rectangular'
                  height={20}
                  sx={{ marginBottom: '20px', borderRadius: '10px', width: '100%' }}
                />
                <Skeleton
                  variant='rectangular'
                  height={250}
                  sx={{ marginBottom: '20px', borderRadius: '10px', width: '100%' }}
                />
                <Skeleton
                  variant='rectangular'
                  height={70}
                  sx={{ marginBottom: '20px', borderRadius: '10px', width: '100%' }}
                />
              </Box>
              <Box className={classes.loadingImageBox}>
                <Skeleton
                  variant='rectangular'
                  height={500}
                  sx={{ borderRadius: '10px', width: '100%' }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {error && (
        <Box className={classes.loadingWrapper}>
          <Box className={classes.loadingContentWrapper}>
            <Box className={classes.errorBox}>
              <InfoOutlinedIcon sx={{ color: COLORS.gray, fontSize: '100px' }} />
              <Box className={classes.errorBoxInfo}>
                <h4 className={fontClasses.h4}>{t('validationsForm.errorTitle')}</h4>
                <p className={fontClasses.p}>{t('validationsForm.errorMessage')}</p>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {projectsData &&
        !loading &&
        !error &&
        projectsData.map((project, index) => (
          <Box key={project.documentId}>
            {' '}
            {/* ✅ Strapi 5 usa documentId */}
            <ProjectCard
              index={index}
              title={project.title}
              summary={project.summary}
              images={project.images}
              ideas={project.ideas}
              beneficiaries={project.beneficiaries}
              months={project.months}
              editions={project.editions}
              communityTotal={project.communityTotal}
              zones={project.zones}
              tag={project.tag}
              inProgress={project.inProgress}
            />
          </Box>
        ))}
    </Box>
  );
};

const Projects = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Box>
      <Metatags
        title='CEMCA | Proyectos'
        description='Te invitamos a explorar una selección de proyectos emblemáticos en los que hemos trabajado.'
      />
      <Header />
      <ProjectsContent />
    </Box>
  );
};

export default Projects;
